import React, { useRef, useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useSearch } from "@/contexts/SearchContext";
import { colorToHex as _sharedColorToHex } from "@/constants/colors";
import { WAREHOUSES, WAREHOUSE_LABEL, WAREHOUSE_SHORT, WAREHOUSE_FLAG, STOCK_FACTORIES, FACTORY_SHORT, warehouseTotals, factoryTotals, stockGrid, cellTotal, emptyGridRow, emptyWarehouseRow } from "@/constants/warehouses";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useT } from "@/i18n/LanguageContext";
import { ClampSelect } from "@/components/ClampSelect";
import { VehiclesEditor, emptyVehicle, vehiclesOf, normalizeVehicles, vehicleYearStr, vehicleLabelOf } from "@/components/VehiclesEditor";
import { DeleteCatalogButton } from "@/components/DeleteCatalogButton";
import {
  Upload,
  Download,
  Search,
  Package,
  Layers,
  Palette,
  Car,
  Ruler,
  Wrench,
  Boxes,
  FileSpreadsheet,
  X,
  Warehouse,
  Plus,
  AlertTriangle,
  Sparkles,
  MapPin,
  RefreshCcw,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Info,
  Pencil,
  Bell,
  ShieldCheck,
} from "lucide-react";

const BRAND = {
  red: "#C30000",
  redHover: "#A30000",
  redTint: "#FFE9E9",
  ink: "#0A0A0A",
  line: "#E7E7EB",
  fog: "#F5F5F7",
};

export default function Inventory() {
  const { user } = useAuth();
  const { t } = useT();
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({
    queryKey: ["inventory"],
    queryFn: async () => (await api.get("/inventory")).data,
  });
  const [importOpen, setImportOpen] = useState(false);
  const [replaceAll, setReplaceAll] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const [addOpen, setAddOpen] = useState(false);
  const [finderOpen, setFinderOpen] = useState(false);
  const [stockRow, setStockRow] = useState(null);
  const [detailCat, setDetailCat] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [changeLogOpen, setChangeLogOpen] = useState(false);
  const navigate = useNavigate();
  const openBatchOrders = () => navigate("/batch-orders?new=1&below=1");
  const [viewFilter, setViewFilter] = useState("all"); // "all" | "instock" | "belowreorder"
  const { query: search, setQuery: setSearch, setPlaceholder } = useSearch();
  const [warehouseFilter, setWarehouseFilter] = useState("all");
  const [showLowOnly, setShowLowOnly] = useState(false);

  // Update the header search placeholder to reflect Inventory-specific fields
  useEffect(() => {
    setPlaceholder("Search inventory — CAT # · Brand · Model · Year · F1 · F2 · Sub-trim · Color · Bin…");
  }, [setPlaceholder]);

  // Inventory Packing may open this tab; Super Admin decides read-only (default) vs editable.
  const { data: access } = useQuery({
    queryKey: ["inventory-access"],
    queryFn: async () => (await api.get("/inventory/access")).data,
    staleTime: 30_000,
  });
  const isInvPacking = user.role === "inventory_packing";
  const isSuperAdmin = user.role === "super_admin";
  const invLocked = access ? !!access.locked : true;
  const canManage = user.role === "back_office" || isSuperAdmin || (isInvPacking && !invLocked);
  const canExport = isSuperAdmin;
  const canEdit = canManage;
  const canReorder = canEdit && !isInvPacking; // purchasing decision stays with Back Office / Super Admin
  const toggleLock = useMutation({
    mutationFn: (locked) => api.put("/inventory/access-lock", { locked }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["inventory-access"] }); toast.success(r?.data?.locked ? "Inventory is now READ-ONLY for Inventory Packing" : "Inventory Packing can now edit the Inventory"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const toggleExpand = (cat) => setExpandedRows((p) => ({ ...p, [cat]: !p[cat] }));

  const doExport = async () => {
    try {
      const resp = await api.get("/inventory/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `leprecon-inventory-${stamp}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.length} catalog item(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  const [importProgress, setImportProgress] = useState("");
  const submitImport = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Choose an Excel file first"); return; }
    setUploading(true);
    setImportProgress("Uploading…");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: job } = await api.post(`/inventory/import?replace_all=${replaceAll}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // The import runs in the background — poll until it finishes so a slow
      // proxy can't cut the connection half-way through a large catalog.
      let res = job;
      while (res.status === "running") {
        await new Promise((r) => setTimeout(r, 1500));
        res = (await api.get(`/inventory/import/${job.job_id}`)).data;
        setImportProgress(res.progress || "Importing…");
      }
      if (res.status === "error") throw new Error(res.error || "Import failed");
      toast.success(`Imported ${res.inserted} new · ${res.updated} updated (${res.total} total)`);
      setImportOpen(false);
      if (fileRef.current) fileRef.current.value = "";
      qc.invalidateQueries({ queryKey: ["inventory"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Import failed");
    } finally {
      setUploading(false);
      setImportProgress("");
    }
  };

  const stats = useMemo(() => {
    const totalCats = data.length;
    const totalSubTrims = data.reduce((s, i) => s + (i.sub_trims?.length || 0), 0);
    const inStockCount = data.filter((i) => (i.current_stock ?? i.qty ?? 0) > 0).length;
    const totalStock = data.reduce((s, i) => s + (i.current_stock ?? i.qty ?? 0), 0);
    const belowReorder = data.filter((i) => {
      const cur = i.current_stock ?? i.qty ?? 0;
      const res = i.reserved_stock ?? 0;
      const avail = Math.max(0, cur - res);
      return (i.reorder_point ?? 0) > 0 && avail <= i.reorder_point;
    }).length;
    return { totalCats, totalSubTrims, inStockCount, totalStock, belowReorder };
  }, [data]);

  const warehouses = ["all", ...WAREHOUSES];

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return data.filter((i) => {
      if (warehouseFilter !== "all" && (warehouseTotals(i)[warehouseFilter] || 0) <= 0) return false;
      const cur = i.current_stock ?? i.qty ?? 0;
      const res = i.reserved_stock ?? 0;
      const avail = Math.max(0, cur - res);
      const isBelow = (i.reorder_point ?? 0) > 0 && avail <= i.reorder_point;
      if (viewFilter === "instock" && cur <= 0) return false;
      if (viewFilter === "belowreorder" && !isBelow) return false;
      if (showLowOnly && !isBelow) return false;
      if (!s) return true;
      const subTrimStrings = (i.sub_trims || []).flatMap((st) =>
        typeof st === "string" ? [st] : [st.trim, ...(st.years || [])],
      );
      const haystack = [
        i.cat_number, i.brand, i.model, i.year, i.trim, i.gen, i.color, i.warehouse, i.bin_location,
        i.front_cover_code, i.f_clamp_code, i.rear_cover_code, i.r_clamp_code,
        i.front_cover_code_f2, i.f_clamp_code_f2, i.rear_cover_code_f2, i.r_clamp_code_f2,
        i.spacers, i.spacers_qty, i.min_rim_size,
        i.current_stock, i.reserved_stock, i.min_qty, i.max_qty, i.reorder_point,
        ...subTrimStrings,
        ...(i.colors_available || []),
      ]
        .filter((v) => v !== undefined && v !== null)
        .map((v) => String(v).toLowerCase())
        .join(" | ");
      return haystack.includes(s);
    });
  }, [data, search, warehouseFilter, showLowOnly]);

  const detail = detailCat ? data.find((d) => d.cat_number === detailCat) : null;

  // CAT #s that share the same front + rear cover codes (F1 or F2 pair) — one product under two numbers
  const dupMap = useMemo(() => {
    const norm = (v) => String(v || "").trim().toUpperCase();
    const groups = {};
    for (const i of data) {
      for (const [f, r] of [["front_cover_code", "rear_cover_code"], ["front_cover_code_f2", "rear_cover_code_f2"]]) {
        if (norm(i[f]) && norm(i[r])) (groups[`${norm(i[f])}|${norm(i[r])}`] ||= new Set()).add(i.cat_number);
      }
    }
    const out = {};
    for (const set of Object.values(groups)) {
      if (set.size < 2) continue;
      for (const c of set) out[c] = [...new Set([...(out[c] || []), ...[...set].filter((x) => x !== c)])];
    }
    return out;
  }, [data]);

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-neutral-500 font-mono mb-1">
            <FileSpreadsheet size={12} /> {t("Master Catalog · Vehicle → Product Database")}
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight" style={{ color: BRAND.ink }}>
            {t("Inventory Management")}
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            {t("Suggest the right caliper cover for any vehicle. Track stock, reserved, reorder points and bin locations.")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isInvPacking && (
            <span
              data-testid="inv-readonly-badge"
              className={`h-10 inline-flex items-center gap-1.5 px-3 rounded-md border text-xs font-bold uppercase tracking-widest ${invLocked ? "border-slate-400 bg-slate-100 text-slate-700" : "border-emerald-500 bg-emerald-50 text-emerald-800"}`}
              title={invLocked ? "Super Admin has locked the Inventory for your station — view only" : "Super Admin allowed your station to edit the Inventory"}
            >
              {invLocked ? "🔒 Read only" : "✏️ Editing allowed"}
            </span>
          )}
          {isSuperAdmin && access && (
            <Button
              data-testid="inv-lock-toggle"
              variant="outline"
              onClick={() => toggleLock.mutate(!invLocked)}
              disabled={toggleLock.isPending}
              className={`h-10 gap-2 text-xs ${invLocked ? "border-slate-400" : "border-emerald-500 text-emerald-800"}`}
              title={`Inventory Packing station: ${invLocked ? "read-only (click to allow editing)" : "can edit (click to lock as read-only)"}${access.changed_by ? ` · set by ${access.changed_by}` : ""}`}
            >
              {invLocked ? "🔒 Inv Packing: read-only" : "✏️ Inv Packing: can edit"}
            </Button>
          )}
          <Button
            data-testid="inv-finder-btn"
            variant="outline"
            onClick={() => setFinderOpen(true)}
            className="h-10 gap-2"
          >
            <Sparkles size={16} style={{ color: BRAND.red }} /> Cover Finder
          </Button>
          {canEdit && !isInvPacking && (
            <ChangeLogBellButton onClick={() => setChangeLogOpen(true)} isSuperAdmin={user.role === "super_admin"} />
          )}
          {canManage && (
            <>
              {canReorder && (
                <Button
                  data-testid="inv-batch-order-btn"
                  variant="outline"
                  onClick={() => navigate("/batch-orders?new=1")}
                  className="h-10 gap-2 border-indigo-400 text-indigo-800 hover:bg-indigo-50"
                  title="Order sets for stock straight from production (Batch Orders)"
                >
                  📦 Batch order for stock
                </Button>
              )}
              <Button
                data-testid="inv-add-btn"
                variant="outline"
                onClick={() => setAddOpen(true)}
                className="h-10 gap-2"
              >
                <Plus size={16} /> Add Inventory
              </Button>
              <Button
                data-testid="inv-import-btn"
                onClick={() => setImportOpen(true)}
                className="h-10 px-4 gap-2 font-medium text-white shadow-sm"
                style={{ background: BRAND.red }}
                onMouseEnter={(e) => (e.currentTarget.style.background = BRAND.redHover)}
                onMouseLeave={(e) => (e.currentTarget.style.background = BRAND.red)}
              >
                <Upload size={16} /> Import Excel
              </Button>
              {canExport && (
                <Button
                  data-testid="inv-export-btn"
                  variant="outline"
                  onClick={doExport}
                  className="h-10 gap-2"
                  title="Super Admin only — exports every catalog item incl. zero-stock"
                >
                  <Download size={16} /> Export Excel
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Metrics — clickable filter tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <MetricCard
          icon={Package}
          label="Catalog Items"
          value={stats.totalCats}
          testid="stat-cats"
          active={viewFilter === "all"}
          onClick={() => setViewFilter("all")}
          hint="Show all catalog items"
        />
        <MetricCard
          icon={Boxes}
          label="Total Stock"
          value={stats.totalStock}
          sublabel={`${stats.inStockCount} CAT # with stock`}
          testid="stat-stock"
          active={viewFilter === "instock"}
          onClick={() => setViewFilter(viewFilter === "instock" ? "all" : "instock")}
          hint="Only show items that have stock"
        />
        <MetricCard
          icon={AlertTriangle}
          label="Below Reorder"
          value={stats.belowReorder}
          testid="stat-low"
          highlight={stats.belowReorder > 0}
          active={viewFilter === "belowreorder"}
          onClick={() => {
            setViewFilter("belowreorder");
            if (canReorder && stats.belowReorder > 0) openBatchOrders();
          }}
          hint="Open Batch Orders pre-filled with the low-stock items"
        />
      </div>

      {/* Filters — search moved to the global header bar */}
      <div className="rounded-xl bg-white border shadow-sm p-3 flex items-center gap-3 flex-wrap" style={{ borderColor: BRAND.line }}>
        <div className="flex items-center gap-2">
          <Warehouse size={14} className="text-neutral-400" />
          <select
            data-testid="inv-warehouse-filter"
            value={warehouseFilter}
            onChange={(e) => setWarehouseFilter(e.target.value)}
            className="text-sm bg-white border rounded-md px-2.5 py-1.5 outline-none capitalize"
            style={{ borderColor: BRAND.line }}
          >
            {warehouses.map((w) => (
              <option key={w} value={w}>{w === "all" ? "All warehouses" : `${WAREHOUSE_FLAG[w]} ${WAREHOUSE_LABEL[w]} stock`}</option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-1.5 text-sm text-neutral-700 cursor-pointer">
          <input
            data-testid="inv-low-only"
            type="checkbox"
            checked={showLowOnly}
            onChange={(e) => setShowLowOnly(e.target.checked)}
          />
          <AlertTriangle size={14} style={{ color: BRAND.red }} /> Only below reorder
        </label>
        <div className="text-xs font-mono text-neutral-500 ml-auto">
          {filtered.length} of {data.length} shown
        </div>
      </div>

      {/* Active-filter banner */}
      {viewFilter !== "all" && (
        <div
          className="rounded-lg border px-4 py-2 flex items-center gap-3 text-sm"
          style={{ borderColor: BRAND.red, background: BRAND.redTint, color: BRAND.red }}
          data-testid="filter-banner"
        >
          <AlertTriangle size={14} />
          <span className="font-medium">
            Filter active — {viewFilter === "instock" ? "showing only CAT # with stock" : "showing only CAT # below reorder point"}
          </span>
          {viewFilter === "belowreorder" && canReorder && (
            <Button
              data-testid="filter-banner-reorder"
              size="sm"
              onClick={openBatchOrders}
              className="ml-auto h-7 text-xs gap-1 text-white"
              style={{ background: BRAND.red }}
            >
              <Plus size={11} /> Batch order for stock
            </Button>
          )}
          <button
            data-testid="filter-banner-clear"
            onClick={() => setViewFilter("all")}
            className={`text-xs underline font-medium ${viewFilter === "belowreorder" && canReorder ? "" : "ml-auto"}`}
          >
            Clear filter
          </button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl bg-white border shadow-sm overflow-hidden" style={{ borderColor: BRAND.line }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="inventory-table">
            <thead>
              <tr className="text-left" style={{ background: BRAND.fog }}>
                <Th icon={FileSpreadsheet}>CAT #</Th>
                <Th icon={Car}>Vehicle</Th>
                <Th icon={Layers}>Fits</Th>
                <Th icon={Wrench}>Factory 1 Codes</Th>
                <Th>Factory 2 Codes</Th>
                <Th>Spacer</Th>
                <Th icon={Ruler}>Min Rim</Th>
                <Th icon={Boxes}>Current</Th>
                <Th className="text-right pr-4">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={9} className="px-4 py-14 text-center text-neutral-400">Loading catalog…</td></tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-14 text-center">
                    <div className="inline-flex flex-col items-center gap-2 text-neutral-400">
                      <Package size={28} strokeWidth={1.4} />
                      <div className="text-sm">
                        {data.length === 0
                          ? "No catalog items yet — click Import Excel or Add Inventory to begin."
                          : "No matches for that filter."}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              {filtered.map((i) => {
                const cur = i.current_stock ?? i.qty ?? 0;
                const res = i.reserved_stock ?? 0;
                const avail = Math.max(0, cur - res);
                const isLow = (i.reorder_point ?? 0) > 0 && avail <= i.reorder_point;
                const isOpen = !!expandedRows[i.cat_number];
                return (
                  <React.Fragment key={i.id || i.cat_number}>
                    <tr
                      className={`border-t transition-colors ${isLow ? "" : "hover:bg-neutral-50"}`}
                      style={{ borderColor: BRAND.line, background: isLow ? BRAND.redTint : undefined }}
                      data-testid={`inv-row-${i.cat_number}`}
                    >
                      <td className="px-3 py-3">
                        <span className="font-mono font-bold text-base text-neutral-900 tracking-wide">{i.cat_number}</span>
                        {dupMap[i.cat_number] && (
                          <button
                            type="button"
                            data-testid={`dup-badge-${i.cat_number}`}
                            onClick={() => setEditRow(i)}
                            title={`Same front + rear cover codes as CAT # ${dupMap[i.cat_number].join(", ")} — open Edit to merge into one product`}
                            className="mt-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100"
                          >
                            ⚠ {t("same covers as")} {dupMap[i.cat_number].join(", ")}
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="font-semibold text-neutral-900">{i.brand}</div>
                        <div className="text-xs text-neutral-500 mt-0.5">{i.model} · {i.year}</div>
                        {vehiclesOf(i).length > 1 && (
                          <div className="text-[10px] text-neutral-500 mt-0.5 font-mono" data-testid={`inv-also-fits-${i.cat_number}`}>
                            + {vehiclesOf(i).length - 1} more car{vehiclesOf(i).length > 2 ? "s" : ""}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <button
                          data-testid={`inv-subtrims-${i.cat_number}`}
                          onClick={() => setDetailCat(i.cat_number)}
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-neutral-800 hover:text-black rounded-md px-2 py-1 border transition-colors bg-white"
                          style={{ borderColor: BRAND.line }}
                          title="Cars this cover fits"
                        >
                          <Car size={12} />
                          {vehiclesOf(i).length}
                        </button>
                      </td>
                      <ReadableCodeCell
                        front={i.front_cover_code}
                        rear={i.rear_cover_code}
                        stock={factoryTotals(i).factory_1}
                        factory="factory_1"
                        testid={`row-f1-stock-${i.cat_number}`}
                      />
                      <ReadableCodeCell
                        front={i.front_cover_code_f2}
                        rear={i.rear_cover_code_f2}
                        stock={factoryTotals(i).factory_2}
                        factory="factory_2"
                        testid={`row-f2-stock-${i.cat_number}`}
                      />
                      <td className="px-3 py-3">
                        {i.spacers ? (
                          <span
                            className="inline-flex items-baseline gap-1.5 font-mono text-xs px-2 py-0.5 rounded"
                            style={{ background: BRAND.redTint, color: BRAND.red }}
                          >
                            <span className="font-bold">{i.spacers}</span>
                            <span className="opacity-70">×</span>
                            <span className="font-bold">{i.spacers_qty ?? "—"}</span>
                          </span>
                        ) : <span className="text-neutral-300">—</span>}
                      </td>
                      <td className="px-3 py-3 font-mono font-semibold">{i.min_rim_size ? `${i.min_rim_size}"` : "—"}</td>
                      <td className="px-3 py-3">
                        <WarehouseStockSplit item={i} total={cur} />
                        {isLow && (
                          <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: BRAND.red, color: "#fff" }}>
                            LOW
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex justify-end gap-1.5">
                          <Button
                            data-testid={`info-btn-${i.cat_number}`}
                            size="sm"
                            variant="outline"
                            onClick={() => toggleExpand(i.cat_number)}
                            className="h-7 text-xs gap-1 px-2"
                            title="Show / hide extra details"
                          >
                            <Info size={11} /> Info.
                            {isOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                          </Button>
                          {canEdit && (
                            <Button
                              data-testid={`edit-btn-${i.cat_number}`}
                              size="sm"
                              variant="outline"
                              onClick={() => setEditRow(i)}
                              className="h-7 text-xs gap-1 px-2"
                              title="Edit codes, trims, or CAT# (requires confirmation)"
                            >
                              <Pencil size={11} /> Edit
                            </Button>
                          )}
                          {canManage && (
                            <Button
                              data-testid={`stock-btn-${i.cat_number}`}
                              size="sm"
                              variant="outline"
                              onClick={() => setStockRow(i)}
                              className="h-7 text-xs gap-1 px-2"
                              title="Adjust colour stock"
                            >
                              <RefreshCcw size={11} /> Stock
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr
                        className="border-t"
                        style={{ borderColor: BRAND.line, background: BRAND.fog }}
                        data-testid={`inv-info-panel-${i.cat_number}`}
                      >
                        <td colSpan={9} className="px-6 py-4">
                          <InfoPanel item={i} avail={avail} isLow={isLow} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      <ImportModal open={importOpen} onOpenChange={setImportOpen} fileRef={fileRef} replaceAll={replaceAll} setReplaceAll={setReplaceAll} uploading={uploading} progress={importProgress} onSubmit={submitImport} />
      <AddInventoryModal
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={() => { qc.invalidateQueries({ queryKey: ["inventory"] }); }}
      />
      <StockModal
        row={stockRow}
        onOpenChange={(v) => { if (!v) setStockRow(null); }}
        onSaved={() => { qc.invalidateQueries({ queryKey: ["inventory"] }); setStockRow(null); }}
      />
      <CoverFinderModal
        open={finderOpen}
        onOpenChange={setFinderOpen}
      />
      <SubTrimsModal
        detail={detail}
        onOpenChange={(v) => { if (!v) setDetailCat(null); }}
      />
      <EditInventoryModal
        row={editRow}
        dupOf={editRow ? dupMap[editRow.cat_number] || [] : []}
        onOpenChange={(v) => { if (!v) setEditRow(null); }}
        onSaved={() => { qc.invalidateQueries({ queryKey: ["inventory"] }); setEditRow(null); }}
      />
      <ChangeLogModal
        open={changeLogOpen}
        onOpenChange={setChangeLogOpen}
        isSuperAdmin={user.role === "super_admin"}
      />
    </div>
  );
}

// ---------- Cover Finder ----------
function CoverFinderModal({ open, onOpenChange }) {
  const [form, setForm] = useState({ brand: "", model: "", year: "", front_cover_code: "", rear_cover_code: "", color: "" });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) { setResults([]); setForm({ brand: "", model: "", year: "", front_cover_code: "", rear_cover_code: "", color: "" }); }
  }, [open]);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/inventory/suggest", form);
      setResults(data.results || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={18} style={{ color: BRAND.red }} /> Cover Finder — Suggest the correct caliper cover
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <LabeledInput label="Brand" testid="finder-brand" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} placeholder="e.g. Acura" />
            <LabeledInput label="Model" testid="finder-model" value={form.model} onChange={(v) => setForm({ ...form, model: v })} placeholder="e.g. ILX" />
            <LabeledInput label="Year" testid="finder-year" value={form.year} onChange={(v) => setForm({ ...form, year: v })} placeholder="e.g. 2020" />
            <LabeledInput label="Front Cover Code" testid="finder-front" value={form.front_cover_code} onChange={(v) => setForm({ ...form, front_cover_code: v })} placeholder="e.g. HO 37 SS" />
            <LabeledInput label="Rear Cover Code" testid="finder-rear" value={form.rear_cover_code} onChange={(v) => setForm({ ...form, rear_cover_code: v })} placeholder="e.g. HO 20 SS" />
            <LabeledInput label="Color (optional)" testid="finder-color" value={form.color} onChange={(v) => setForm({ ...form, color: v })} placeholder="e.g. Red" />
          </div>
          <Button
            data-testid="finder-run"
            onClick={run}
            disabled={loading}
            className="w-full h-11 gap-2 text-white font-medium"
            style={{ background: BRAND.red }}
          >
            <Search size={16} /> {loading ? "Searching…" : "Find Matching Covers"}
          </Button>
          <div className="max-h-[45vh] overflow-y-auto space-y-2">
            {results.length === 0 && !loading && (
              <div className="text-sm text-neutral-400 py-6 text-center">
                Enter vehicle details or code(s) and click Find. Best matches show first.
              </div>
            )}
            {results.map((r, idx) => (
              <div
                key={r.item.cat_number || idx}
                className="rounded-lg border p-3 flex items-start gap-3"
                style={{ borderColor: BRAND.line }}
                data-testid={`finder-result-${r.item.cat_number}`}
              >
                <div className="w-14 h-14 rounded-lg flex flex-col items-center justify-center flex-shrink-0"
                     style={{ background: r.score >= 60 ? BRAND.redTint : BRAND.fog }}>
                  <div className="text-[10px] uppercase font-mono text-neutral-500">Score</div>
                  <div className="text-lg font-bold font-mono" style={{ color: r.score >= 60 ? BRAND.red : BRAND.ink }}>{r.score}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-mono font-semibold text-sm text-neutral-900">{r.item.cat_number} · {r.item.brand} {r.item.model} <span className="text-neutral-500">{r.item.year}</span></div>
                  <div className="text-xs text-neutral-600 mt-0.5">
                    F1: <span className="font-mono">{r.item.front_cover_code || "—"} / {r.item.rear_cover_code || "—"}</span>
                    {r.item.front_cover_code_f2 && <> · F2: <span className="font-mono">{r.item.front_cover_code_f2 || "—"} / {r.item.rear_cover_code_f2 || "—"}</span></>}
                    · Spacer <span className="font-mono">{r.item.spacers} × {r.item.spacers_qty}</span>
                    · Min Rim <span className="font-mono">{r.item.min_rim_size}&quot;</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {r.reasons.map((rs, i) => (
                      <span key={i} className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: BRAND.fog, color: BRAND.ink }}>{rs}</span>
                    ))}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase font-mono text-neutral-500">Available</div>
                  <div className="font-mono font-bold text-lg">{r.item.available_stock ?? 0}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Add Inventory ----------
function AddInventoryModal({ open, onOpenChange, onCreated }) {
  const empty = {
    cat_number: "",
    color: "", front_cover_code: "", f_clamp_code: "", rear_cover_code: "", r_clamp_code: "",
    front_cover_code_f2: "", f_clamp_code_f2: "", rear_cover_code_f2: "", r_clamp_code_f2: "",
    min_rim_size: "", spacers: "", spacers_qty: 0,
    current_stock: 0, min_qty: 0, max_qty: 0, reorder_point: 0, bin_location: "",
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [colors, setColors] = useState([]);
  const [vehicles, setVehicles] = useState([emptyVehicle()]);

  useEffect(() => {
    if (open) {
      setForm(empty);
      setVehicles([emptyVehicle()]);
      api.get("/inventory/colors").then((r) => setColors(r.data.colors || [])).catch(() => {});
    }
  }, [open]);

  const first = vehicles[0] || emptyVehicle();
  const suggestCat = async () => {
    if (!first.brand || !first.model) { toast.error("Enter brand and model first"); return; }
    const { data } = await api.get(`/inventory/next-cat-number`, { params: { brand: first.brand, model: first.model } });
    setForm((f) => ({ ...f, cat_number: data.cat_number }));
    toast.success(`Suggested CAT# ${data.cat_number}`);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    // Basic validation
    const required = ["cat_number", "color", "front_cover_code", "f_clamp_code", "rear_cover_code", "r_clamp_code", "min_rim_size"];
    for (const req of required) {
      if (!String(form[req] || "").trim()) { toast.error(`Field required: ${req}`); return; }
    }
    const vs = normalizeVehicles(vehicles);
    if (!vs.length || !vs[0].brand || !vs[0].model) { toast.error("Enter the car this cover fits: brand + model"); return; }
    if (!vs[0].year_from) { toast.error("Field required: year from"); return; }
    if (vs.some((v) => !v.brand || !v.model || !v.year_from)) { toast.error("Every car needs brand, model and a from-year"); return; }

    setSaving(true);
    try {
      // Initial stock goes into per-warehouse × factory buckets for the chosen colour
      const init = Object.fromEntries(WAREHOUSES.map((w) => [w, Object.fromEntries(STOCK_FACTORIES.map((f) => [f, Math.max(0, parseInt(form[`initial_stock_${w}_${f}`] || 0) || 0)]))]));
      const initTotal = WAREHOUSES.reduce((s, w) => s + cellTotal(init[w]), 0);
      const stockGridPayload = form.color && initTotal > 0 ? { [form.color]: init } : null;
      const initBins = Object.fromEntries(WAREHOUSES.filter((w) => (form[`initial_bin_${w}`] || "").trim()).map((w) => [w, form[`initial_bin_${w}`].trim()]));
      const binPayload = form.color && Object.keys(initBins).length ? { [form.color]: initBins } : null;
      await api.post("/inventory", {
        cat_number: form.cat_number,
        brand: vs[0].brand,
        model: vs[0].model,
        year: vehicleYearStr(vs[0]),
        vehicles: vs,
        color: form.color,
        front_cover_code: form.front_cover_code,
        f_clamp_code: form.f_clamp_code,
        rear_cover_code: form.rear_cover_code,
        r_clamp_code: form.r_clamp_code,
        front_cover_code_f2: form.front_cover_code_f2 || null,
        f_clamp_code_f2: form.f_clamp_code_f2 || null,
        rear_cover_code_f2: form.rear_cover_code_f2 || null,
        r_clamp_code_f2: form.r_clamp_code_f2 || null,
        min_rim_size: form.min_rim_size,
        spacers: form.spacers || null,
        spacers_qty: parseInt(form.spacers_qty || 0),
        current_stock: initTotal,
        stock_by_warehouse_factory: stockGridPayload,
        bin_by_warehouse: binPayload,
        min_qty: parseInt(form.min_qty || 0),
        max_qty: parseInt(form.max_qty || 0),
        reorder_point: parseInt(form.reorder_point || 0),
        bin_location: form.bin_location || null,
      });
      toast.success("Inventory item created");
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create item");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus size={18} style={{ color: BRAND.red }} /> Add Inventory — new catalog item
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-2">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-mono text-neutral-500 mb-2">Fits vehicles · brand, model, from year → to year (add every car this cover fits)</div>
            <VehiclesEditor rows={vehicles} onChange={setVehicles} testid="add-vehicle" />
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-widest font-mono text-neutral-500 mb-2">Color</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-semibold text-neutral-600 uppercase tracking-widest">Color</label>
                <select
                  data-testid="add-color"
                  value={form.color}
                  onChange={(e) => set("color", e.target.value)}
                  className="mt-1 w-full h-10 rounded-md border px-3 text-sm bg-white outline-none"
                  style={{ borderColor: BRAND.line }}
                >
                  <option value="">— Select a color —</option>
                  {colors.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-widest font-mono text-neutral-500 mb-2">CAT #</div>
            <div className="flex gap-2">
              <Input data-testid="add-cat" value={form.cat_number} onChange={(e) => set("cat_number", e.target.value)} placeholder="Auto-generated or type manually" className="flex-1" />
              <Button data-testid="add-cat-suggest" variant="outline" onClick={suggestCat} className="gap-1">
                <Sparkles size={14} style={{ color: BRAND.red }} /> Suggest
              </Button>
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-widest font-mono text-neutral-500 mb-2">Factory 1 Codes</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <LabeledInput label="Front Cover" testid="add-f1-front" value={form.front_cover_code} onChange={(v) => set("front_cover_code", v)} />
              <LabeledClamp label="F. Clamp" testid="add-f1-fclamp" value={form.f_clamp_code} onChange={(v) => set("f_clamp_code", v)} />
              <LabeledInput label="Rear Cover" testid="add-f1-rear" value={form.rear_cover_code} onChange={(v) => set("rear_cover_code", v)} />
              <LabeledClamp label="R. Clamp" testid="add-f1-rclamp" value={form.r_clamp_code} onChange={(v) => set("r_clamp_code", v)} />
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-widest font-mono text-neutral-500 mb-2">Factory 2 Codes (optional)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <LabeledInput label="Front Cover" testid="add-f2-front" value={form.front_cover_code_f2} onChange={(v) => set("front_cover_code_f2", v)} />
              <LabeledClamp label="F. Clamp" testid="add-f2-fclamp" value={form.f_clamp_code_f2} onChange={(v) => set("f_clamp_code_f2", v)} />
              <LabeledInput label="Rear Cover" testid="add-f2-rear" value={form.rear_cover_code_f2} onChange={(v) => set("rear_cover_code_f2", v)} />
              <LabeledClamp label="R. Clamp" testid="add-f2-rclamp" value={form.r_clamp_code_f2} onChange={(v) => set("r_clamp_code_f2", v)} />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <LabeledInput label="Spacer Type" testid="add-spacer" value={form.spacers} onChange={(v) => set("spacers", v)} placeholder="3MM" />
            <LabeledInput label="Spacer Qty" testid="add-spacer-qty" value={form.spacers_qty} onChange={(v) => set("spacers_qty", v)} type="number" />
            <LabeledInput label="Min Rim" testid="add-min-rim" value={form.min_rim_size} onChange={(v) => set("min_rim_size", v)} placeholder="19" />
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-widest font-mono text-neutral-500 mb-2">Stock Setup · initial stock of {form.color || "the chosen colour"} per warehouse · F1 (black) / F2 (white) covers</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="add-initial-stock-grid">
              {WAREHOUSES.map((w) => (
                <div key={w} className="rounded-md border p-2" style={{ borderColor: BRAND.line }}>
                  <div className="text-[11px] font-semibold text-neutral-700 mb-1.5">{WAREHOUSE_FLAG[w]} {WAREHOUSE_LABEL[w]}</div>
                  <div className="grid grid-cols-2 gap-2">
                    {STOCK_FACTORIES.map((f) => (
                      <LabeledInput key={f} label={FACTORY_SHORT[f]} testid={`add-initial-${w}-${f}`} value={form[`initial_stock_${w}_${f}`] ?? ""} onChange={(v) => set(`initial_stock_${w}_${f}`, v)} type="number" />
                    ))}
                  </div>
                  <div className="mt-2">
                    <LabeledInput label={`Bin · ${WAREHOUSE_LABEL[w]}`} testid={`add-initial-bin-${w}`} value={form[`initial_bin_${w}`] ?? ""} onChange={(v) => set(`initial_bin_${w}`, v)} placeholder="A-12" />
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              <LabeledInput label="Min Qty" testid="add-min-qty" value={form.min_qty} onChange={(v) => set("min_qty", v)} type="number" />
              <LabeledInput label="Max Qty" testid="add-max-qty" value={form.max_qty} onChange={(v) => set("max_qty", v)} type="number" />
              <LabeledInput label="Reorder Point" testid="add-reorder" value={form.reorder_point} onChange={(v) => set("reorder_point", v)} type="number" />
              <LabeledInput label="Default Bin" testid="add-bin" value={form.bin_location} onChange={(v) => set("bin_location", v)} placeholder="A-12 (fallback)" />
            </div>
            <div className="mt-2 text-[10px] text-neutral-500 flex items-center gap-1.5" data-testid="stock-setup-hint">
              <ShieldCheck size={11} style={{ color: BRAND.red }} />
              Stock and bin are kept per warehouse — Canada, USA and China — per colour, and stock per factory codes (F1 / F2). Total = sum of all.
            </div>
          </div>

          <Button
            data-testid="add-submit"
            onClick={submit}
            disabled={saving}
            className="w-full h-11 gap-2 text-white font-medium"
            style={{ background: BRAND.red }}
          >
            <Plus size={16} /> {saving ? "Creating…" : "Create Inventory Item"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Stock Modal ----------
function StockModal({ row, onOpenChange, onSaved }) {
  const [form, setForm] = useState({});
  const [colors, setColors] = useState([]);
  const [stock, setStock] = useState({}); // { colour: { canada: {factory_1, factory_2}, usa: {…}, china: {…} } }
  const [bins, setBins] = useState({}); // { colour: { canada: "A-12", usa: "", china: "" } }

  useEffect(() => {
    if (row) {
      setForm({
        current_stock: row.current_stock ?? row.qty ?? 0,
        reserved_stock: row.reserved_stock ?? 0,
        min_qty: row.min_qty ?? 0,
        max_qty: row.max_qty ?? 0,
        reorder_point: row.reorder_point ?? 0,
        bin_location: row.bin_location ?? "",
      });
      const init = {};
      for (const [c, per] of Object.entries(stockGrid(row))) {
        init[c] = emptyGridRow();
        for (const w of WAREHOUSES) for (const f of STOCK_FACTORIES) init[c][w][f] = parseInt(per?.[w]?.[f] || 0) || 0;
      }
      setStock(init);
      const b = {};
      for (const [c, per] of Object.entries(row.bin_by_warehouse || {})) b[c] = Object.fromEntries(WAREHOUSES.map((w) => [w, per?.[w] || ""]));
      setBins(b);
    }
  }, [row]);

  useEffect(() => {
    if (row && colors.length === 0) {
      api.get("/inventory/colors").then((r) => setColors(r.data.colors || []));
    }
  }, [row, colors.length]);

  const [saving, setSaving] = useState(false);
  const n = (v) => Math.max(0, parseInt(v || 0) || 0);
  const totals = emptyWarehouseRow();
  const fTotals = { factory_1: 0, factory_2: 0 };
  for (const per of Object.values(stock)) for (const w of WAREHOUSES) for (const f of STOCK_FACTORIES) { totals[w] += n(per?.[w]?.[f]); fTotals[f] += n(per?.[w]?.[f]); }
  const grandTotal = WAREHOUSES.reduce((s, w) => s + totals[w], 0);

  const submit = async () => {
    if (!row) return;
    setSaving(true);
    try {
      const grid = {};
      const binGrid = {};
      for (const c of colors) {
        const per = Object.fromEntries(WAREHOUSES.map((w) => [w, Object.fromEntries(STOCK_FACTORIES.map((f) => [f, n(stock[c]?.[w]?.[f])]))]));
        if (WAREHOUSES.some((w) => cellTotal(per[w]) > 0)) grid[c] = per;
        const b = Object.fromEntries(WAREHOUSES.filter((w) => (bins[c]?.[w] || "").trim()).map((w) => [w, bins[c][w].trim()]));
        if (Object.keys(b).length) binGrid[c] = b;
      }
      await api.post(`/inventory/${row.id}/stock`, {
        ...form,
        reserved_stock: parseInt(form.reserved_stock || 0),
        min_qty: parseInt(form.min_qty || 0),
        max_qty: parseInt(form.max_qty || 0),
        reorder_point: parseInt(form.reorder_point || 0),
        stock_by_warehouse_factory: grid,
        bin_by_warehouse: binGrid,
      });
      toast.success("Stock updated");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setQty = (c, w, f, v) => setStock((prev) => {
    const per = { ...emptyGridRow(), ...(prev[c] || {}) };
    return { ...prev, [c]: { ...per, [w]: { ...per[w], [f]: v } } };
  });
  const setBin = (c, w, v) => setBins((prev) => ({ ...prev, [c]: { canada: "", usa: "", china: "", ...(prev[c] || {}), [w]: v } }));
  const grid = "minmax(130px, 1fr) 84px 1.3fr 1.3fr 1.3fr"; // Colour · Total · Canada · USA · China

  return (
    <Dialog open={!!row} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap pr-8">
            <RefreshCcw size={18} style={{ color: BRAND.red }} /> Adjust Stock — <span className="font-mono">{row?.cat_number}</span>
            <Button data-testid="stock-save" onClick={submit} disabled={saving} className="ml-auto h-10 px-5 gap-2 text-white font-medium" style={{ background: BRAND.red }}>
              <RefreshCcw size={16} /> {saving ? "Saving…" : "Save Changes"}
            </Button>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-mono text-neutral-500 mb-2">Stock levels</div>
            <div className="grid grid-cols-3 gap-3">
              <LabeledInput label="Reserved Stock" testid="stock-reserved" value={form.reserved_stock} onChange={(v) => set("reserved_stock", v)} type="number" />
              <LabeledInput label="Min Qty" testid="stock-min" value={form.min_qty} onChange={(v) => set("min_qty", v)} type="number" />
              <LabeledInput label="Max Qty" testid="stock-max" value={form.max_qty} onChange={(v) => set("max_qty", v)} type="number" />
              <LabeledInput label="Reorder Point" testid="stock-reorder" value={form.reorder_point} onChange={(v) => set("reorder_point", v)} type="number" />
              <LabeledInput label="Default Bin" testid="stock-bin" value={form.bin_location} onChange={(v) => set("bin_location", v)} placeholder="A-12 (fallback)" />
              <div>
                <label className="text-[11px] font-semibold text-neutral-600 uppercase tracking-widest">Current (auto)</label>
                <div className="mt-1 h-9 rounded-md border flex items-center px-3 font-mono font-bold" style={{ borderColor: BRAND.line, background: BRAND.fog }} data-testid="stock-current-auto">
                  {grandTotal}
                  <span className="ml-2 text-[10px] font-normal text-neutral-500">
                    ({WAREHOUSES.map((w) => `${WAREHOUSE_SHORT[w]} ${totals[w]}`).join(" · ")} · F1 {fTotals.factory_1} · F2 {fTotals.factory_2})
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
              <div className="text-[13px] font-bold uppercase tracking-widest text-neutral-800">Stock by Colour · per Warehouse · per Factory codes · Bin</div>
              <div className="text-[10px] font-mono text-neutral-500 flex items-center gap-2">
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm border" style={{ borderColor: BRAND.line, background: "#fff" }} /> F1 = black number</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-neutral-900" /> F2 = white number</span>
                <span className="inline-flex items-center gap-1"><MapPin size={10} /> Bin per colour + warehouse</span>
              </div>
            </div>
            <div className="border rounded-lg" style={{ borderColor: BRAND.line }} data-testid="stock-warehouse-grid">
              {/* Header: +15% type, frozen while the dialog scrolls (sticky inside the 70vh scroller) */}
              <div
                className="grid items-center text-[12.65px] font-bold uppercase tracking-widest font-mono text-neutral-700 border-b sticky top-0 z-10 rounded-t-lg shadow-sm"
                style={{ borderColor: BRAND.line, background: BRAND.fog, gridTemplateColumns: grid }}
                data-testid="stock-grid-header"
              >
                <div className="px-3 py-3">Colour</div>
                <div className="px-3 py-3 text-right border-l" style={{ borderColor: BRAND.line, color: BRAND.red }} data-testid="stock-grid-header-total">Total</div>
                {WAREHOUSES.map((w) => (
                  <div key={w} className="px-3 py-3 border-l" style={{ borderColor: BRAND.line }}>
                    <div className="flex items-center gap-1.5"><span>{WAREHOUSE_FLAG[w]}</span> {WAREHOUSE_LABEL[w]}</div>
                    <div className="grid gap-1.5 mt-1 text-[10.35px] text-neutral-500" style={{ gridTemplateColumns: "1fr 1fr 1.4fr" }}><span>F1</span><span>F2</span><span>Bin</span></div>
                  </div>
                ))}
              </div>
              <div className="divide-y" style={{ borderColor: BRAND.line }}>
                {colors.map((c) => {
                  const testKey = c.replace(/\s+/g, "-").replace(/\./g, "").toLowerCase();
                  const per = stock[c] || emptyGridRow();
                  const rowTotal = WAREHOUSES.reduce((s, w) => s + STOCK_FACTORIES.reduce((t, f) => t + n(per[w]?.[f]), 0), 0);
                  return (
                    <div key={c} className="grid items-center hover:bg-neutral-50/50 transition-colors" style={{ borderColor: BRAND.line, gridTemplateColumns: grid }} data-testid={`stock-row-${testKey}`}>
                      <div className="px-3 py-2 flex items-center gap-2.5">
                        <span className="w-5 h-5 rounded-full border-2 shrink-0" style={{ background: colorToHex(c), borderColor: "#00000020" }} />
                        <span className="text-sm font-medium text-neutral-800">{c}</span>
                      </div>
                      <div className="px-3 py-2 text-right border-l font-mono text-lg font-bold" style={{ borderColor: BRAND.line, color: rowTotal > 0 ? BRAND.red : "#c0c0c0" }} data-testid={`color-sum-${testKey}`}>
                        {rowTotal}
                      </div>
                      {WAREHOUSES.map((w) => (
                        <div key={w} className="px-2 py-2 border-l grid gap-1.5" style={{ borderColor: BRAND.line, gridTemplateColumns: "1fr 1fr 1.4fr" }}>
                          {STOCK_FACTORIES.map((f) => (
                            <Input
                              key={f}
                              data-testid={`color-qty-${w}-${f}-${testKey}`}
                              type="number"
                              min="0"
                              value={per[w]?.[f] ?? 0}
                              onChange={(e) => setQty(c, w, f, e.target.value)}
                              className={`h-9 text-sm text-right font-mono font-semibold ${f === "factory_2" ? "bg-neutral-900 text-white border-neutral-900 placeholder:text-neutral-400" : ""}`}
                              title={`${FACTORY_SHORT[f]} covers · ${WAREHOUSE_LABEL[w]}`}
                            />
                          ))}
                          <div className="relative">
                            <MapPin size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
                            <Input
                              data-testid={`color-bin-${w}-${testKey}`}
                              value={bins[c]?.[w] ?? ""}
                              onChange={(e) => setBin(c, w, e.target.value)}
                              placeholder="Bin"
                              className="h-9 text-xs font-mono pl-6"
                              title={`Bin / shelf of ${c} in ${WAREHOUSE_LABEL[w]}`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
              <div className="grid items-center border-t text-sm font-bold" style={{ borderColor: BRAND.line, background: BRAND.fog, gridTemplateColumns: grid }}>
                <div className="px-3 py-3 uppercase tracking-widest text-[11px] font-mono text-neutral-500">Grand Total</div>
                <div className="px-3 py-3 text-right border-l font-mono text-xl" style={{ borderColor: BRAND.line, color: BRAND.red }} data-testid="stock-grand-total">
                  {grandTotal}
                </div>
                {WAREHOUSES.map((w) => (
                  <div key={w} className="px-3 py-3 border-l flex items-center justify-between" style={{ borderColor: BRAND.line }}>
                    <span className="uppercase tracking-widest text-[10px] font-mono text-neutral-500">{WAREHOUSE_SHORT[w]}</span>
                    <span className="font-mono text-neutral-900" data-testid={`stock-total-${w}`}>{totals[w]}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Row Current column: total + Canada / USA / China split ----------
function WarehouseStockSplit({ item, total }) {
  const t = warehouseTotals(item);
  return (
    <div className="inline-flex flex-col items-start" data-testid={`row-stock-${item.cat_number}`}>
      <span className="font-mono font-bold text-lg text-neutral-900 leading-none">{total}</span>
      {total > 0 && (
        <span className="mt-0.5 text-[10px] font-mono text-neutral-500 whitespace-nowrap flex items-center gap-1">
          {WAREHOUSES.filter((w) => t[w] > 0).map((w, i) => (
            <React.Fragment key={w}>
              {i > 0 && <span className="text-neutral-300">·</span>}
              <span className="inline-flex items-center gap-0.5" title={WAREHOUSE_LABEL[w]}>
                <span>{WAREHOUSE_FLAG[w]}</span>
                <span className="font-bold text-neutral-800">{t[w]}</span>
              </span>
            </React.Fragment>
          ))}
        </span>
      )}
    </div>
  );
}


function colorToHex(name) {
  // Delegates to the shared 17-color palette (constants/colors.js) so a single
  // source of truth drives every swatch in the app.
  return _sharedColorToHex(name);
}

// ---------- Fits-vehicles modal (cars a CAT # fits) ----------
function SubTrimsModal({ detail, onOpenChange }) {
  const vehicles = detail ? vehiclesOf(detail) : [];
  return (
    <Dialog open={!!detail} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-baseline gap-2 flex-wrap">
            <span className="font-mono">{detail?.cat_number}</span>
            <span className="text-neutral-400">·</span>
            <span>{detail?.brand} {detail?.model}</span>
            <span className="text-neutral-500 font-mono text-sm">{detail?.year}</span>
            <span className="ml-auto text-xs font-mono text-neutral-500">
              fits {vehicles.length} car{vehicles.length === 1 ? "" : "s"}
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto pr-1 space-y-1.5" data-testid="vehicles-list">
          {vehicles.map((v, idx) => (
            <div
              key={idx}
              className="rounded-md px-3 py-2 border flex items-center gap-3"
              style={{ borderColor: BRAND.line, background: idx % 2 === 0 ? "white" : BRAND.fog }}
              data-testid={`vehicle-row-${idx}`}
            >
              <span className="text-[10px] font-mono text-neutral-400 w-6 shrink-0">{String(idx + 1).padStart(2, "0")}</span>
              <Car size={14} className="text-neutral-500 shrink-0" />
              <span className="flex-1 text-sm text-neutral-800"><b>{v.brand}</b> {v.model}</span>
              <span className="text-[11px] font-mono font-semibold px-2 py-0.5 rounded" style={{ background: BRAND.redTint, color: BRAND.red }}>{vehicleYearStr(v) || "—"}</span>
            </div>
          ))}
          {vehicles.length === 0 && (
            <div className="text-sm text-neutral-400 py-6 text-center">No vehicle recorded — press Edit to add brand, model and years.</div>
          )}
          <div className="text-[10px] text-neutral-500 font-mono pt-2">Edit → “Fits vehicles” to add another car or year range to this CAT #.</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Import Modal ----------
function ImportModal({ open, onOpenChange, fileRef, replaceAll, setReplaceAll, uploading, progress, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!uploading) onOpenChange(v); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload size={18} style={{ color: BRAND.red }} /> Import Inventory Catalog
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg p-3 text-xs text-neutral-600" style={{ background: BRAND.fog }}>
            Upload the Leprecon+ master Excel or a file downloaded with Export. Every row under a CAT# is a car it fits (brand · model · year or year range) — rows are merged into the "Fits vehicles" list. Colour cells: dark fill = Factory 1 stock, white = Factory 2, "8 / 2" = both.
          </div>
          <input
            ref={fileRef}
            data-testid="inv-import-file"
            type="file"
            accept=".xlsx"
            className="block w-full text-sm border rounded-lg file:mr-3 file:py-2 file:px-3 file:border-0 file:font-semibold file:text-white file:cursor-pointer cursor-pointer"
            style={{ borderColor: BRAND.line }}
          />
          <style>{`input[type=file]::file-selector-button { background: ${BRAND.red}; }`}</style>
          <label className="flex items-start gap-2 text-sm">
            <input
              data-testid="inv-import-replace"
              type="checkbox"
              checked={replaceAll}
              onChange={(e) => setReplaceAll(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium">Replace all existing inventory</span>
              <span className="block text-xs text-neutral-500">Destructive — wipes current catalog before importing.</span>
            </span>
          </label>
          <Button
            data-testid="inv-import-submit"
            onClick={onSubmit}
            disabled={uploading}
            className="w-full h-11 gap-2 text-white font-medium"
            style={{ background: BRAND.red }}
          >
            {uploading ? (progress || "Importing…") : (<><Upload size={16} /> Upload &amp; Import</>)}
          </Button>
          {uploading && (
            <p className="text-xs text-neutral-500 text-center" data-testid="inv-import-progress">
              Large catalogs take a minute — the import keeps running on the server even if this takes a while.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Reusables ----------
function Th({ children, icon: Icon, className = "" }) {
  return (
    <th className={`px-3 py-2.5 text-[10px] font-semibold text-neutral-500 uppercase tracking-wider whitespace-nowrap ${className}`}>
      <span className="inline-flex items-center gap-1.5">
        {Icon && <Icon size={11} className="text-neutral-400" />}
        {children}
      </span>
    </th>
  );
}

// ---------- Readable Code Cell (larger, more legible for factory floor use) ----------
function ReadableCodeCell({ front, rear, stock, factory, testid }) {
  const qty = stock?.total || 0;
  const isF2 = factory === "factory_2";
  if (!front && !rear && qty <= 0) return <td className="px-3 py-3 text-neutral-300">—</td>;
  return (
    <td className="px-3 py-3">
      <div className="flex items-center gap-3">
        <div className="flex flex-col gap-0.5 font-mono text-sm">
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] font-bold uppercase text-neutral-400 w-4">F</span>
            <span className="font-bold text-neutral-900 tracking-wide">{front || <span className="text-neutral-300 font-normal">—</span>}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] font-bold uppercase text-neutral-400 w-4">R</span>
            <span className="font-bold text-neutral-900 tracking-wide">{rear || <span className="text-neutral-300 font-normal">—</span>}</span>
          </div>
        </div>
        {stock && (
          <div
            className={`rounded-md px-2 py-1 text-center min-w-[44px] ${isF2 ? "bg-neutral-900 text-white" : "bg-white text-neutral-900 border"}`}
            style={isF2 ? {} : { borderColor: BRAND.line }}
            title={`${FACTORY_SHORT[factory]} covers in stock · ${WAREHOUSES.map((w) => `${WAREHOUSE_LABEL[w]} ${stock[w] || 0}`).join(" · ")}`}
            data-testid={testid}
          >
            <div className={`font-mono font-bold text-lg leading-none ${qty > 0 ? "" : "opacity-30"}`}>{qty}</div>
            {qty > 0 && (
              <div className={`text-[10px] font-mono mt-0.5 whitespace-nowrap ${isF2 ? "text-neutral-300" : "text-neutral-500"}`}>
                {WAREHOUSES.filter((w) => (stock[w] || 0) > 0).map((w) => `${WAREHOUSE_FLAG[w]}${stock[w]}`).join(" ")}
              </div>
            )}
          </div>
        )}
      </div>
    </td>
  );
}

function CodeCell({ primary, secondary }) {
  if (!primary && !secondary) return <td className="px-3 py-2.5 text-neutral-300">—</td>;
  return (
    <td className="px-3 py-2.5">
      <span className="font-mono text-xs text-neutral-900 font-semibold">
        {primary || "—"}
        <span className="text-neutral-300 mx-1.5">/</span>
        {secondary || "—"}
      </span>
    </td>
  );
}
function MetricCard({ icon: Icon, label, value, sublabel, testid, highlight, onClick, active, hint }) {
  const isClickable = !!onClick;
  const borderColor = active ? "var(--brand-red)" : (highlight ? "var(--brand-red)" : "var(--brand-line)");
  const background = active ? "var(--brand-red-tint)" : (highlight ? "var(--brand-red-tint)" : "white");
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!isClickable}
      data-testid={testid}
      data-active={active || undefined}
      title={hint}
      className={`text-left rounded-xl border shadow-sm p-4 w-full flex items-center gap-3 transition-all duration-150 ${
        isClickable ? "cursor-pointer hover:shadow-md hover:-translate-y-[1px]" : "cursor-default"
      } ${active ? "ring-2 ring-offset-1" : ""}`}
      style={{
        borderColor,
        background,
        ...(active ? { boxShadow: `0 0 0 2px ${BRAND.red}22, 0 1px 2px rgba(0,0,0,0.05)` } : {}),
      }}
    >
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: active || highlight ? "white" : "var(--brand-red-tint)", color: "var(--brand-red)" }}
      >
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono flex items-center gap-1.5">
          {label}
          {active && (
            <span className="text-[9px] px-1.5 py-0.5 rounded font-bold" style={{ background: BRAND.red, color: "white" }}>
              ACTIVE
            </span>
          )}
        </div>
        <div className="text-2xl font-display font-bold text-neutral-900 leading-tight">{value}</div>
        {sublabel && <div className="text-[10px] text-neutral-500 mt-0.5">{sublabel}</div>}
      </div>
    </button>
  );
}
function LabeledInput({ label, testid, value, onChange, placeholder, type = "text" }) {
  const { t } = useT();
  return (
    <div>
      <label className="text-[11px] font-semibold text-neutral-600 uppercase tracking-widest">{t(label)}</label>
      <Input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1" />
    </div>
  );
}

function LabeledClamp({ label, testid, value, onChange }) {
  const { t } = useT();
  return (
    <div>
      <label className="text-[11px] font-semibold text-neutral-600 uppercase tracking-widest">{t(label)}</label>
      <ClampSelect testid={testid} value={value} onChange={onChange} className="mt-1" />
    </div>
  );
}

// ---------- Trim Multi-Select (drop list beside Color) ----------

// ---------- Info Panel (drop-down bottom row: hidden details) ----------
function InfoPanel({ item, avail, isLow }) {
  const stock = item.stock_by_color || {};
  const grid = stockGrid(item);
  const binsAll = item.bin_by_warehouse || {};
  const whTotals = warehouseTotals(item);
  const fTotals = factoryTotals(item);
  const colorEntries = Object.entries(stock).filter(([, v]) => (v ?? 0) > 0);
  const zeroColors = 17 - colorEntries.length;
  const info = [
    { label: "Available", value: avail, mono: true, tone: isLow ? "danger" : "default" },
    { label: "Reserved", value: item.reserved_stock ?? 0, mono: true },
    { label: "Min / Max", value: `${item.min_qty ?? 0} / ${item.max_qty ?? "—"}`, mono: true },
    { label: "Reorder Point", value: `${item.reorder_point ?? 0}${isLow ? " ⚠" : ""}`, mono: true, tone: isLow ? "danger" : "default" },
    { label: "Default Bin", value: item.bin_location || "—", mono: true },
    { label: "Warehouses", value: WAREHOUSES.map((w) => `${WAREHOUSE_FLAG[w]} ${whTotals[w]}`).join("  "), mono: true },
    { label: "F1 / F2 covers", value: `${fTotals.factory_1.total} / ${fTotals.factory_2.total}`, mono: true },
    { label: "Spare parts", value: `🧩 ${item.spare_parts_qty ?? 0}`, mono: true },
  ];
  const clamps = [
    { label: "F1 F.Clamp", value: item.f_clamp_code },
    { label: "F1 R.Clamp", value: item.r_clamp_code },
    { label: "F2 F.Clamp", value: item.f_clamp_code_f2 },
    { label: "F2 R.Clamp", value: item.r_clamp_code_f2 },
  ];
  return (
    <div className="space-y-4" data-testid={`info-body-${item.cat_number}`}>
      <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
        {info.map((k) => (
          <div key={k.label} className="rounded-md border bg-white px-3 py-2" style={{ borderColor: BRAND.line }}>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">{k.label}</div>
            <div
              className={`mt-1 ${k.mono ? "font-mono" : ""} font-bold text-neutral-900`}
              style={k.tone === "danger" ? { color: BRAND.red } : {}}
            >
              {k.value}
            </div>
          </div>
        ))}
      </div>

      <div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-2">
          <Palette size={12} /> Colour Stock Breakdown ({colorEntries.length}/17 with stock)
          <span className="ml-2 inline-flex items-center gap-2 text-neutral-400">
            {WAREHOUSES.map((w) => <span key={w} className="inline-flex items-center gap-1">{WAREHOUSE_FLAG[w]} {WAREHOUSE_LABEL[w]}</span>)}
          </span>
        </div>
        {colorEntries.length === 0 ? (
          <div className="text-xs text-neutral-400 italic">No per-colour stock recorded.</div>
        ) : (
          <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-9 gap-2">
            {colorEntries.map(([c, v]) => {
              const per = grid[c] || null;
              const bins = binsAll[c] || {};
              const fmtCell = (w) => {
                const cell = per?.[w] || {};
                const f1 = parseInt(cell.factory_1 || 0) || 0, f2 = parseInt(cell.factory_2 || 0) || 0;
                if (f1 + f2 <= 0 && !bins[w]) return null;
                return `${WAREHOUSE_FLAG[w]}${f1 > 0 ? ` F1:${f1}` : ""}${f2 > 0 ? ` F2:${f2}` : ""}${bins[w] ? ` @${bins[w]}` : ""}`;
              };
              return (
                <div
                  key={c}
                  className="rounded-md border bg-white px-2 py-1.5 flex items-center gap-2"
                  style={{ borderColor: BRAND.line }}
                  data-testid={`info-color-${c.replace(/\s+/g, "-").replace(/\./g, "").toLowerCase()}`}
                  title={per ? WAREHOUSES.map((w) => `${WAREHOUSE_LABEL[w]}: F1 ${per[w]?.factory_1 || 0} · F2 ${per[w]?.factory_2 || 0}${bins[w] ? ` · bin ${bins[w]}` : ""}`).join(" · ") : ""}
                >
                  <span className="w-3 h-3 rounded-full border" style={{ background: colorToHex(c), borderColor: BRAND.line }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-medium text-neutral-600 truncate">{c}</div>
                    <div className="text-xs font-mono font-bold flex items-baseline gap-1.5">
                      <span>{v}</span>
                      {per && (
                        <span className="text-[9px] font-normal text-neutral-500 whitespace-nowrap">
                          {WAREHOUSES.map(fmtCell).filter(Boolean).join(" ")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {zeroColors > 0 && (
              <div className="col-span-3 md:col-span-6 lg:col-span-9 text-[10px] text-neutral-400 font-mono">
                + {zeroColors} more colour(s) with 0 stock
              </div>
            )}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-2">
          <Wrench size={12} /> Clamp Types
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {clamps.map((c) => (
            <div key={c.label} className="rounded-md border bg-white px-3 py-1.5" style={{ borderColor: BRAND.line }}>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">{c.label}</div>
              <div className="mt-0.5 font-mono text-sm font-bold text-neutral-900">{c.value || <span className="text-neutral-300 font-normal">—</span>}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Edit Inventory Modal (with second-approval popup) ----------
function EditInventoryModal({ row, onOpenChange, onSaved, dupOf = [] }) {
  const open = !!row;
  const [form, setForm] = useState(null);
  const [vehicles, setVehicles] = useState([emptyVehicle()]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDiff, setPendingDiff] = useState([]);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [impact, setImpact] = useState(null);
  const { user: me } = useAuth();
  const isSuperAdmin = me?.role === "super_admin";

  useEffect(() => {
    if (!row) { setForm(null); setVehicles([emptyVehicle()]); setConfirmOpen(false); setReason(""); return; }
    setForm({
      cat_number: row.cat_number || "",
      gen: row.gen || "",
      front_cover_code: row.front_cover_code || "",
      f_clamp_code: row.f_clamp_code || "",
      rear_cover_code: row.rear_cover_code || "",
      r_clamp_code: row.r_clamp_code || "",
      front_cover_code_f2: row.front_cover_code_f2 || "",
      f_clamp_code_f2: row.f_clamp_code_f2 || "",
      rear_cover_code_f2: row.rear_cover_code_f2 || "",
      r_clamp_code_f2: row.r_clamp_code_f2 || "",
      spacers: row.spacers || "",
      spacers_qty: row.spacers_qty ?? "",
      min_rim_size: row.min_rim_size || "",
      bin_location: row.bin_location || "",
    });
    setVehicles(vehiclesOf(row));
  }, [row]);

  if (!open || !form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const vehiclesChanged = () => JSON.stringify(normalizeVehicles(vehiclesOf(row))) !== JSON.stringify(normalizeVehicles(vehicles));

  const buildDiff = () => {
    const diff = [];
    const cmp = (k, label) => {
      const oldV = row[k] ?? "";
      const newV = form[k] ?? "";
      if (String(oldV) !== String(newV)) diff.push({ label, before: String(oldV || "—"), after: String(newV || "—") });
    };
    cmp("cat_number", "CAT #");
    cmp("gen", "GEN");
    if (vehiclesChanged()) {
      diff.push({ label: "Fits vehicles", before: normalizeVehicles(vehiclesOf(row)).map(vehicleLabelOf).join(" | ") || "—", after: normalizeVehicles(vehicles).map(vehicleLabelOf).join(" | ") || "—" });
    }
    cmp("front_cover_code", "F1 Front Cover");
    cmp("f_clamp_code", "F1 F.Clamp");
    cmp("rear_cover_code", "F1 Rear Cover");
    cmp("r_clamp_code", "F1 R.Clamp");
    cmp("front_cover_code_f2", "F2 Front Cover");
    cmp("f_clamp_code_f2", "F2 F.Clamp");
    cmp("rear_cover_code_f2", "F2 Rear Cover");
    cmp("r_clamp_code_f2", "F2 R.Clamp");
    cmp("spacers", "Spacer Type");
    cmp("spacers_qty", "Spacer Qty");
    cmp("min_rim_size", "Min Rim");
    cmp("bin_location", "Bin Location");
    return diff;
  };

  const previewSave = async () => {
    if (!form.cat_number.trim()) { toast.error("CAT # cannot be empty"); return; }
    const vs = normalizeVehicles(vehicles);
    if (!vs.length || vs.some((v) => !v.brand || !v.model)) { toast.error("Every car needs a brand and a model"); return; }
    const diff = buildDiff();
    if (diff.length === 0) { toast.info("No changes to save"); return; }
    let imp = null;
    if (form.cat_number.trim() !== (row.cat_number || "")) {
      try {
        imp = (await api.get(`/inventory/${row.id}/cat-impact`, { params: { new_cat: form.cat_number.trim() } })).data;
      } catch (e) { toast.error(e?.response?.data?.detail || "Could not check the CAT # change"); return; }
    }
    setImpact(imp);
    setPendingDiff(diff);
    setConfirmOpen(true);
  };

  const fieldMap = {
    "CAT #": "cat_number", "GEN": "gen",
    "F1 Front Cover": "front_cover_code", "F1 F.Clamp": "f_clamp_code",
    "F1 Rear Cover": "rear_cover_code", "F1 R.Clamp": "r_clamp_code",
    "F2 Front Cover": "front_cover_code_f2", "F2 F.Clamp": "f_clamp_code_f2",
    "F2 Rear Cover": "rear_cover_code_f2", "F2 R.Clamp": "r_clamp_code_f2",
    "Spacer Type": "spacers", "Spacer Qty": "spacers_qty",
    "Min Rim": "min_rim_size", "Bin Location": "bin_location",
  };

  const doSave = async () => {
    setSaving(true);
    try {
      const payload = { reason };
      if (impact?.merge) payload.merge_into_existing = true;
      for (const d of pendingDiff) {
        const key = fieldMap[d.label];
        if (key) {
          let val = form[key];
          if (key === "spacers_qty") val = val === "" ? null : Number(val);
          payload[key] = val;
        }
      }
      if (vehiclesChanged()) {
        const vs = normalizeVehicles(vehicles);
        payload.vehicles = vs;
        payload.brand = vs[0].brand;
        payload.model = vs[0].model;
        payload.year = vehicleYearStr(vs[0]);
      }
      const { data: res } = await api.patch(`/inventory/${row.id}`, payload);
      toast.success(res.message || "Changes saved · Super Admin has been notified");
      setConfirmOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open && !confirmOpen} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Pencil size={18} style={{ color: BRAND.red }} /> Edit Inventory · {row.cat_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div className="rounded-md border p-3 flex items-start gap-2 text-xs" style={{ borderColor: BRAND.red, background: BRAND.redTint, color: BRAND.red }}>
              <ShieldCheck size={16} className="flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-bold">Change control</div>
                <div className="mt-0.5 text-[11px] leading-relaxed">
                  Every edit here requires a second approval on the confirmation pop-up. Once confirmed,
                  Super Admin will be automatically notified.
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <LabeledInput label="CAT #" testid="edit-cat" value={form.cat_number} onChange={(v) => set("cat_number", v)} />
              <LabeledInput label="GEN" testid="edit-gen" value={form.gen} onChange={(v) => set("gen", v)} />
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-widest font-mono text-neutral-500 mb-2">Fits vehicles · brand, model, from year → to year</div>
              <VehiclesEditor rows={vehicles} onChange={setVehicles} testid="edit-vehicle" />
            </div>

            {dupOf.length > 0 && (
              <div className="rounded-md border border-amber-400 bg-amber-50 p-3 text-xs text-amber-900" data-testid="edit-dup-hint">
                <div className="font-bold">⚠ Same front + rear cover codes as CAT # {dupOf.join(", ")}</div>
                <div className="mt-0.5 text-[11px] leading-relaxed">
                  One product under two numbers. Change this CAT # to the other one and both are merged — stock and bins are
                  combined and every order marked <b className="font-mono">{row.cat_number}</b> switches to the new CAT #.
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {dupOf.map((c) => (
                    <Button key={c} size="sm" variant="outline" className="h-7 text-xs font-mono border-amber-400 bg-white" data-testid={`edit-use-cat-${c}`} onClick={() => set("cat_number", c)}>
                      Use {c}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="text-[11px] uppercase tracking-widest font-mono text-neutral-500 mb-2">Factory 1 codes</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <LabeledInput label="Front Cover" testid="edit-f1-front" value={form.front_cover_code} onChange={(v) => set("front_cover_code", v)} />
                <LabeledClamp label="F.Clamp" testid="edit-f1-fclamp" value={form.f_clamp_code} onChange={(v) => set("f_clamp_code", v)} />
                <LabeledInput label="Rear Cover" testid="edit-f1-rear" value={form.rear_cover_code} onChange={(v) => set("rear_cover_code", v)} />
                <LabeledClamp label="R.Clamp" testid="edit-f1-rclamp" value={form.r_clamp_code} onChange={(v) => set("r_clamp_code", v)} />
              </div>
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-widest font-mono text-neutral-500 mb-2">Factory 2 codes</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <LabeledInput label="Front Cover" testid="edit-f2-front" value={form.front_cover_code_f2} onChange={(v) => set("front_cover_code_f2", v)} />
                <LabeledClamp label="F.Clamp" testid="edit-f2-fclamp" value={form.f_clamp_code_f2} onChange={(v) => set("f_clamp_code_f2", v)} />
                <LabeledInput label="Rear Cover" testid="edit-f2-rear" value={form.rear_cover_code_f2} onChange={(v) => set("rear_cover_code_f2", v)} />
                <LabeledClamp label="R.Clamp" testid="edit-f2-rclamp" value={form.r_clamp_code_f2} onChange={(v) => set("r_clamp_code_f2", v)} />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <LabeledInput label="Spacer Type" testid="edit-spacer" value={form.spacers} onChange={(v) => set("spacers", v)} />
              <LabeledInput label="Spacer Qty" testid="edit-spacer-qty" value={form.spacers_qty} onChange={(v) => set("spacers_qty", v)} type="number" />
              <LabeledInput label="Min Rim" testid="edit-min-rim" value={form.min_rim_size} onChange={(v) => set("min_rim_size", v)} />
              <LabeledInput label="Bin Location" testid="edit-bin" value={form.bin_location} onChange={(v) => set("bin_location", v)} />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t" style={{ borderColor: BRAND.line }}>
            {isSuperAdmin && (
              <DeleteCatalogButton row={row} onDeleted={() => { onOpenChange(false); onSaved?.(); }} />
            )}
            <Button data-testid="edit-cancel" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              data-testid="edit-review-btn"
              onClick={previewSave}
              className="gap-1 text-white font-medium"
              style={{ background: BRAND.red }}
              onMouseEnter={(e) => (e.currentTarget.style.background = BRAND.redHover)}
              onMouseLeave={(e) => (e.currentTarget.style.background = BRAND.red)}
            >
              Review & Save →
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmChangesModal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        diff={pendingDiff}
        reason={reason}
        setReason={setReason}
        onConfirm={doSave}
        saving={saving}
        catNumber={row.cat_number}
        impact={impact}
      />
    </>
  );
}

// ---------- Confirm Changes Modal (the "second approval" pop-up) ----------
function ConfirmChangesModal({ open, onOpenChange, diff, reason, setReason, onConfirm, saving, catNumber, impact }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <ShieldCheck size={20} style={{ color: BRAND.red }} /> Confirm changes to {catNumber}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md border p-3 text-sm" style={{ borderColor: BRAND.red, background: BRAND.redTint }}>
            <div className="font-semibold" style={{ color: BRAND.red }}>Please review carefully</div>
            <div className="text-xs mt-1 text-neutral-700">
              Confirming these changes will apply them immediately and send a notification alert to
              Super Admin. This action is logged for audit.
            </div>
          </div>

          {impact && (
            <div className="rounded-md border-2 border-amber-500 bg-amber-50 p-3 text-sm" data-testid="confirm-cat-impact">
              {impact.merge ? (
                <>
                  <div className="font-bold text-amber-900">🔀 MERGE — CAT # {impact.new_cat} already exists ({impact.existing?.brand} {impact.existing?.model} {impact.existing?.year || ""}){impact.same_covers ? " · same cover codes" : ""}</div>
                  <div className="text-xs mt-1 text-amber-900">
                    <b className="font-mono">{impact.old_cat}</b> will be folded into <b className="font-mono">{impact.new_cat}</b> as one product: stock and bins are
                    combined ({impact.merge_stock} already in {impact.new_cat}), <b className="font-mono">{impact.old_cat}</b> is removed from the catalog.
                  </div>
                </>
              ) : (
                <div className="font-bold text-amber-900">CAT # {impact.old_cat} → {impact.new_cat}</div>
              )}
              <div className="mt-1.5 text-xs font-semibold text-amber-900" data-testid="confirm-orders-affected">
                🚗 {impact.orders_affected} order(s) marked <span className="font-mono">{impact.old_cat}</span> will switch to <span className="font-mono">{impact.new_cat}</span>.
              </div>
            </div>
          )}

          <div>
            <div className="text-[11px] uppercase tracking-widest font-mono text-neutral-500 mb-2">
              {diff.length} change(s) to apply
            </div>
            <div className="border rounded-lg overflow-hidden" style={{ borderColor: BRAND.line }}>
              <table className="w-full text-sm" data-testid="confirm-diff-table">
                <thead>
                  <tr style={{ background: BRAND.fog }}>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-500 w-40">Field</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">Before</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">After</th>
                  </tr>
                </thead>
                <tbody>
                  {diff.map((d, idx) => (
                    <tr key={idx} className="border-t" style={{ borderColor: BRAND.line }} data-testid={`diff-row-${idx}`}>
                      <td className="px-3 py-2 font-semibold text-neutral-800">{d.label}</td>
                      <td className="px-3 py-2 font-mono text-xs text-neutral-500 line-through">{d.before || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs font-bold" style={{ color: BRAND.red }}>{d.after || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <label className="text-[11px] font-semibold text-neutral-600 uppercase tracking-widest">Reason (optional)</label>
            <Input
              data-testid="confirm-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Corrected code typo reported by Factory 1"
              className="mt-1"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t" style={{ borderColor: BRAND.line }}>
          <Button data-testid="confirm-cancel" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            data-testid="confirm-approve"
            onClick={onConfirm}
            disabled={saving}
            className="gap-1 text-white font-semibold"
            style={{ background: BRAND.red }}
            onMouseEnter={(e) => (e.currentTarget.style.background = BRAND.redHover)}
            onMouseLeave={(e) => (e.currentTarget.style.background = BRAND.red)}
          >
            <ShieldCheck size={14} /> {saving ? "Saving…" : impact?.merge ? "Confirm Merge & Notify Super Admin" : "Confirm & Notify Super Admin"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Change-Log Bell Button (header) ----------
function ChangeLogBellButton({ onClick, isSuperAdmin }) {
  const { data } = useQuery({
    queryKey: ["inventory-change-log-count", isSuperAdmin],
    queryFn: async () => (await api.get("/inventory/change-log", { params: { unread_only: isSuperAdmin, limit: 100 } })).data,
    refetchInterval: 30000,
  });
  const count = data?.count || 0;
  return (
    <Button
      data-testid="inv-changelog-btn"
      variant="outline"
      onClick={onClick}
      className="h-10 gap-2 relative"
      title={isSuperAdmin ? "Unacknowledged inventory changes" : "My recent inventory changes"}
    >
      <Bell size={16} />
      {isSuperAdmin ? "Alerts" : "My Changes"}
      {count > 0 && (
        <span
          data-testid="changelog-badge"
          className="absolute -top-1.5 -right-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white"
          style={{ background: BRAND.red, minWidth: 20, textAlign: "center" }}
        >
          {count}
        </span>
      )}
    </Button>
  );
}

// ---------- Change Log Modal ----------
function ChangeLogModal({ open, onOpenChange, isSuperAdmin }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["inventory-change-log-full", isSuperAdmin],
    queryFn: async () => (await api.get("/inventory/change-log", { params: { limit: 200 } })).data,
    enabled: open,
  });
  const entries = data?.entries || [];

  const ack = async (cid) => {
    try {
      await api.post(`/inventory/change-log/${cid}/acknowledge`);
      toast.success("Marked as acknowledged");
      qc.invalidateQueries({ queryKey: ["inventory-change-log-full"] });
      qc.invalidateQueries({ queryKey: ["inventory-change-log-count"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to acknowledge");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Bell size={18} style={{ color: BRAND.red }} />
            {isSuperAdmin ? "Inventory Change Alerts" : "My Inventory Changes"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {isLoading && <div className="text-sm text-neutral-400 py-6 text-center">Loading…</div>}
          {!isLoading && entries.length === 0 && (
            <div className="text-sm text-neutral-400 py-10 text-center">
              No inventory changes recorded yet.
            </div>
          )}
          {entries.map((e) => (
            <div
              key={e.id}
              className="rounded-lg border p-4 space-y-2"
              style={{
                borderColor: BRAND.line,
                background: !e.acknowledged_by_super_admin && isSuperAdmin ? BRAND.redTint : "white",
              }}
              data-testid={`changelog-entry-${e.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-mono font-bold text-sm text-neutral-900">
                    {e.cat_number_after}
                    {e.cat_number_before !== e.cat_number_after && (
                      <span className="text-neutral-400 font-normal ml-2">
                        (was {e.cat_number_before})
                      </span>
                    )}
                    <span className="ml-2 text-xs text-neutral-500 font-normal">
                      {e.brand} · {e.model}
                    </span>
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    Changed by <span className="font-semibold">{e.changed_by_email}</span> ({e.changed_by_role})
                    · {new Date(e.changed_at).toLocaleString()}
                  </div>
                </div>
                {isSuperAdmin && !e.acknowledged_by_super_admin && (
                  <Button
                    data-testid={`ack-btn-${e.id}`}
                    size="sm"
                    variant="outline"
                    onClick={() => ack(e.id)}
                    className="text-xs gap-1 h-7"
                  >
                    <ShieldCheck size={11} /> Acknowledge
                  </Button>
                )}
                {e.acknowledged_by_super_admin && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-green-600">
                    <ShieldCheck size={12} /> Acknowledged
                  </span>
                )}
              </div>

              {e.reason && (
                <div className="text-xs italic text-neutral-600 border-l-2 pl-2" style={{ borderColor: BRAND.red }}>
                  “{e.reason}”
                </div>
              )}

              <div className="border rounded overflow-hidden" style={{ borderColor: BRAND.line }}>
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ background: BRAND.fog }}>
                      <th className="text-left px-2 py-1.5 text-[9px] uppercase tracking-wider text-neutral-500 w-32">Field</th>
                      <th className="text-left px-2 py-1.5 text-[9px] uppercase tracking-wider text-neutral-500">Before</th>
                      <th className="text-left px-2 py-1.5 text-[9px] uppercase tracking-wider text-neutral-500">After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(e.diff || []).map((d, i) => (
                      <tr key={i} className="border-t" style={{ borderColor: BRAND.line }}>
                        <td className="px-2 py-1 font-semibold text-neutral-800">{d.label}</td>
                        <td className="px-2 py-1 font-mono text-neutral-500 line-through">{d.before || "—"}</td>
                        <td className="px-2 py-1 font-mono font-bold" style={{ color: BRAND.red }}>{d.after || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

